/**
 * The settings screen for a vocabulary that is NOT the CRM's.
 *
 * `/settings/lookups` administers the five CRM lookup kinds through one screen
 * because they are one service's contract. The scheduling service's
 * `ACTIVITY_TYPE` and the feedback service's `ISSUE_TYPE` are the same contract
 * MIRRORED — separate deployables, separate databases, separate mutations
 * (CLAUDE.md §5a) — so they cannot be folded into that screen's `kind` picker,
 * and until now they had no screen at all. That is why a fresh deployment's
 * calendar offered an empty «نوع فعالیت» with nowhere to go.
 *
 * This is the shared frame. It knows the RULES that are common to every
 * vocabulary in MeshHub — deactivate, never delete; `code` immutable after
 * creation; `is_system` rows relabellable but not deactivatable — and nothing
 * about which vocabulary it is showing.
 */
import { Button, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { Pencil, Plus } from 'lucide-react';
import React, { useState } from 'react';
import {
  ActiveBadge,
  ConfirmDialog,
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  SettingsPage,
  ShowInactiveToggle,
  SystemLock,
  TextField,
} from './SettingsKit.web.jsx';
import { codeFromLabel } from '../../lib/lookupCode.js';
import { formatCount } from '../../lib/format.js';
import { S } from '../../lib/strings.js';

export function VocabularyScreen({
  title,
  hint,
  emptyHint,
  createTitle,
  codePrefix,
  rows = [],
  loading = false,
  busy = false,
  error,
  includeInactive,
  onIncludeInactiveChange,
  onCreate,
  onUpdate,
  onSetActive,
}) {
  const [editing, setEditing] = useState(null); // row | 'new' | null
  const [confirming, setConfirming] = useState(null);

  return (
    <SettingsPage
      title={title}
      hint={S.settings.common.noDelete}
      toolbar={
        <>
          <ShowInactiveToggle checked={includeInactive} onChange={onIncludeInactiveChange} />
          <Button variant="primary" onClick={() => setEditing('new')} className="gap-2">
            <Plus size={15} aria-hidden="true" />
            {S.settings.common.newItem}
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-text-muted">{hint}</p>

      <MutationError error={error} />
      <LoadingOrEmpty
        loading={loading && rows.length === 0}
        empty={!loading && rows.length === 0}
        emptyHint={emptyHint}
      />

      {rows.length > 0 && (
        <>
          <span className="text-sm text-text-muted">{S.contacts.count(formatCount(rows.length))}</span>
          <Table className="mt-4">
            <THead>
              <TR>
                <TH>{S.settings.common.code}</TH>
                <TH>{S.settings.common.labelFa}</TH>
                <TH>{S.settings.common.labelEn}</TH>
                <TH>{S.settings.common.status}</TH>
                <TH>{S.settings.common.actions}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id} className={row.isActive ? '' : 'opacity-60'}>
                  <TD dir="ltr" className="text-start font-medium">
                    {row.code}
                  </TD>
                  <TD>{row.labelFa}</TD>
                  <TD dir="ltr" className="text-start text-text-muted">
                    {row.labelEn || S.app.none}
                  </TD>
                  <TD>
                    <span className="flex items-center gap-2">
                      <ActiveBadge isActive={row.isActive} />
                      <SystemLock isSystem={row.isSystem} />
                    </span>
                  </TD>
                  <TD>
                    <span className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        aria-label={S.app.edit}
                        title={S.app.edit}
                        onClick={() => setEditing(row)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={row.isSystem && row.isActive}
                        title={row.isSystem && row.isActive ? S.settings.common.systemLocked : ''}
                        onClick={() => setConfirming(row)}
                      >
                        {row.isActive ? S.app.deactivate : S.app.activate}
                      </Button>
                    </span>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </>
      )}

      {editing && (
        <VocabularyDialog
          row={editing === 'new' ? null : editing}
          title={createTitle}
          codePrefix={codePrefix}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            if (editing === 'new') await onCreate(values);
            else await onUpdate(editing, values);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={confirming?.isActive ? S.app.deactivate : S.app.activate}
        message={
          confirming?.isActive
            ? S.settings.common.confirmDeactivate(confirming?.labelFa ?? '')
            : S.settings.common.confirmActivate(confirming?.labelFa ?? '')
        }
        danger={confirming?.isActive}
        confirmLabel={confirming?.isActive ? S.app.deactivate : S.app.activate}
        onConfirm={() => onSetActive(confirming, !confirming.isActive)}
      />
    </SettingsPage>
  );
}

/**
 * The LABEL comes first and the code follows it, which is the opposite of the
 * CRM's lookup dialog — and deliberate. `code` is machinery: immutable, never
 * shown to anyone outside this screen, and derivable from the label by the same
 * function the inline creator uses. An administrator who cares can still type
 * one; one who does not should not be blocked on inventing an identifier.
 */
function VocabularyDialog({ row, title, codePrefix, busy, onClose, onSubmit }) {
  const isNew = !row;
  const [labelFa, setLabelFa] = useState(row?.labelFa ?? '');
  const [labelEn, setLabelEn] = useState(row?.labelEn ?? '');
  const [sortOrder, setSortOrder] = useState(row?.sortOrder ?? 0);
  const [code, setCode] = useState(row?.code ?? '');
  const [codeTouched, setCodeTouched] = useState(false);

  const effectiveCode = isNew && !codeTouched ? codeFromLabel(labelFa, codePrefix) : code;

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={isNew ? title : `${S.app.edit} — ${row.labelFa}`}
      busy={busy}
      onSubmit={() =>
        onSubmit(
          isNew
            ? { code: effectiveCode, labelFa, labelEn, sortOrder: Number(sortOrder) }
            : { labelFa, labelEn, sortOrder: Number(sortOrder) },
        )
      }
    >
      <TextField
        label={S.settings.common.labelFa}
        value={labelFa}
        required
        autoFocus
        onChange={(event) => setLabelFa(event.target.value)}
      />
      <TextField
        label={S.settings.common.code}
        value={effectiveCode}
        dir="ltr"
        required={isNew}
        disabled={!isNew}
        hint={isNew ? S.settings.lookups.codeHint : S.settings.common.codeImmutable}
        onChange={(event) => {
          setCodeTouched(true);
          setCode(event.target.value.toUpperCase());
        }}
      />
      <TextField
        label={S.settings.common.labelEn}
        value={labelEn}
        dir="ltr"
        onChange={(event) => setLabelEn(event.target.value)}
      />
      <TextField
        label={S.settings.common.sortOrder}
        type="number"
        value={sortOrder}
        onChange={(event) => setSortOrder(event.target.value)}
      />
      {row?.isSystem && <p className="text-xs text-text-muted">{S.settings.common.systemLocked}</p>}
    </FormDialog>
  );
}
